import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCode, FiUpload, FiFile, FiInfo } = FiIcons;

const CodeInput = ({ htmlCode, setHtmlCode, cssCode, setCssCode }) => {
  const htmlFileRef = useRef(null);
  const cssFileRef = useRef(null);
  const fullHtmlFileRef = useRef(null);

  const handleFileUpload = (file, setCode) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCode(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleHtmlFileUpload = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file, setHtmlCode);
  };

  const handleCssFileUpload = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file, setCssCode);
  };

  const handleFullHtmlFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        
        // Extract CSS from <style> tags
        const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
        let match;
        let extractedStyles = '';
        let cleanedHtml = content;

        // Extract all style tags content
        while ((match = styleRegex.exec(content)) !== null) {
          extractedStyles += match[1] + '\n';
        }

        if (extractedStyles) {
          setCssCode(extractedStyles);
        }

        // Set HTML content
        setHtmlCode(content);
      };
      reader.readAsText(file);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <SafeIcon icon={FiCode} className="text-slate-600 text-xl" />
        <h2 className="text-xl font-semibold text-slate-800">Code Input</h2>
      </div>

      <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
        <div className="flex items-start gap-3">
          <SafeIcon icon={FiInfo} className="text-blue-600 text-lg mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800 mb-1">You can now paste complete HTML with embedded styles</h3>
            <p className="text-sm text-blue-700">
              Paste your full HTML including &lt;style&gt; tags, and the converter will automatically extract the CSS.
            </p>
            <button 
              onClick={() => fullHtmlFileRef.current?.click()} 
              className="mt-2 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              <SafeIcon icon={FiUpload} className="text-sm" />
              Upload Complete HTML File
            </button>
            <input 
              ref={fullHtmlFileRef} 
              type="file" 
              accept=".html,.htm" 
              onChange={handleFullHtmlFileUpload} 
              className="hidden" 
            />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* HTML Input */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-slate-700">HTML Code *</label>
            <button
              onClick={() => htmlFileRef.current?.click()}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              <SafeIcon icon={FiUpload} className="text-sm" />
              Upload HTML File
            </button>
          </div>
          <input
            ref={htmlFileRef}
            type="file"
            accept=".html,.htm"
            onChange={handleHtmlFileUpload}
            className="hidden"
          />
          <textarea
            value={htmlCode}
            onChange={(e) => setHtmlCode(e.target.value)}
            placeholder="Paste your HTML code here or upload a file..."
            rows={12}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-mono text-sm resize-none"
          />
          {htmlCode && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <SafeIcon icon={FiFile} className="text-xs" />
              {htmlCode.length} characters
            </div>
          )}
        </div>

        {/* CSS Input */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-slate-700">CSS Code (Optional)</label>
            <button
              onClick={() => cssFileRef.current?.click()}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              <SafeIcon icon={FiUpload} className="text-sm" />
              Upload CSS File
            </button>
          </div>
          <input
            ref={cssFileRef}
            type="file"
            accept=".css"
            onChange={handleCssFileUpload}
            className="hidden"
          />
          <textarea
            value={cssCode}
            onChange={(e) => setCssCode(e.target.value)}
            placeholder="Paste your CSS code here or upload a file..."
            rows={12}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-mono text-sm resize-none"
          />
          {cssCode && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <SafeIcon icon={FiFile} className="text-xs" />
              {cssCode.length} characters
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
        <p className="text-sm text-green-800">
          <strong>Tip:</strong> Provide clean, semantic HTML and CSS for the best conversion results. The AI will convert your code to native Elementor widgets and styling options.
        </p>
      </div>
    </motion.div>
  );
};

export default CodeInput;